import { lazy, Suspense, useContext, useState, useEffect } from 'react';
import { getRouteApi, Link } from '@tanstack/react-router';
import styles from './Intro.module.scss';
import { DestinationContext } from '../../contexts/DestinationContext';
import { getMapDataByCountry } from '../../server/getMapDataByCountry';
import MapSkeleton from '../utils/Maps/MapSkeleton';

const LazyLeafletMap = lazy(() => import('../utils/Maps/LeafletMap'));

const routeApi = getRouteApi('/');

const Intro = () => {
  const destination = useContext(DestinationContext);
  const { aboutDescription } = routeApi.useLoaderData();
  const [mapData, setMapData] = useState(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const about = destination?.about;
  const destinationId = destination?._id;

  useEffect(() => {
    if (!destinationId) {
      setMapLoaded(true);
      return;
    }
    let cancelled = false;
    getMapDataByCountry({ data: { destinationId } })
      .then((data) => {
        if (!cancelled) {
          setMapData(data);
          setMapLoaded(true);
        }
      })
      .catch(() => {
        if (!cancelled) setMapLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [destinationId]);

  if (!about?.title) return null;

  const description = aboutDescription ?? about.description;

  const firstMarker = Array.isArray(mapData)
    ? mapData[0]
    : ((mapData?.currentDestination && mapData.currentDestination[0]) || (mapData?.otherDestinations && mapData.otherDestinations[0]));

  const mapLat = firstMarker?.location?.latitude ?? destination?.location?.latitude;
  const mapLng = firstMarker?.location?.longitude ?? destination?.location?.longitude;
  const mapZoom = 9;

  const isMultipleLists = mapData && !Array.isArray(mapData) && (mapData.currentDestination || mapData.otherDestinations);
  const hasMapData = (Array.isArray(mapData) && mapData.length > 0) || 
    (isMultipleLists && (
      (mapData.currentDestination && mapData.currentDestination.length > 0) ||
      (mapData.otherDestinations && mapData.otherDestinations.length > 0)
    ));

  // If destination has location coordinates or markers are still loading, reserve the map slot
  // right from SSR so the 2-column grid is stable and causes 0 Cumulative Layout Shift.
  const hasLocation = Boolean(destination?.location?.latitude && destination?.location?.longitude);
  const showMapSection = hasLocation || hasMapData || (!mapLoaded && Boolean(destinationId));

  return (
    <div className={styles.section}>
      <div className={`${styles.container} ${showMapSection ? styles.withMap : ''}`}>
        <div className={styles.copy}>
          {about.eyebrow && <p className={styles.text}>{about.eyebrow}</p>}
          <h2 className={styles.title} dangerouslySetInnerHTML={{ __html: about.title }} />
          <div className={styles.content}>
            {description && (
              <div className={`${styles.description} inserted-html`} dangerouslySetInnerHTML={{ __html: description }} />
            )}

            {Array.isArray(about.stats) && about.stats.length > 0 && (
              <div className={styles.statrow}>
                {about.stats.map((stat, i) => (
                  <div className={styles.stat} key={stat.label || i}>
                    <div className={styles.statNumber}>{stat.number}</div>
                    <div className={styles.statLabel}>{stat.label}</div>
                    {stat.sublabel && <div className={styles.statSublabel}>{stat.sublabel}</div>}
                  </div>
                ))}
              </div>
            )}

            <Link className={styles.seeMore} to="/useful-info">
              See More →
            </Link>
          </div>
        </div>

        {showMapSection && (
          <div className={styles.mapWrap}>
            {mapData && mapLat && mapLng ? (
              <Suspense fallback={<MapSkeleton label="Loading map markers…" />}>
                <LazyLeafletMap
                  latitude={mapLat}
                  longitude={mapLng}
                  zoom={mapZoom}
                  markerData={mapData}
                />
              </Suspense>
            ) : (
              <MapSkeleton label="Exploring destination map…" />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Intro;
